import { toast } from "@altimateai/lego";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";
import { EMAIL_DOMAIN_NOT_PERMITTED } from "@/pages/subscriptions/services/types";

// Shared save-error handler. Discriminates on the
// `EMAIL_DOMAIN_NOT_PERMITTED` 422 payload from the per-tenant allowlist
// and pins the message inline on the supplied form field; otherwise
// falls back to a destructive toast. Mirrors the discriminator shape
// used for the dbt Cloud `CONFLICT_ERROR` payload.
//
// Used by both the Subscriptions wizard (`useSubscriptionFormHandlers`)
// and the Studio Schedule dialog (`useScheduleForm`).
export const handleEmailDomainSaveError = <T extends FieldValues>(
  setError: UseFormSetError<T>,
  fieldName: Path<T>,
  error: unknown,
  fallbackTitle: string,
  onTracked?: (msg: string) => void
): void => {
  const errorObj = error as Record<string, unknown>;
  if (
    errorObj &&
    typeof errorObj === "object" &&
    errorObj.error_code === EMAIL_DOMAIN_NOT_PERMITTED &&
    typeof errorObj.message === "string"
  ) {
    setError(fieldName, { type: "manual", message: errorObj.message });
    onTracked?.(errorObj.message);
    return;
  }
  // Fallback for non-matching rejections. The shared axios interceptor
  // delivers errors as either a string (`detail` / `message` shortcut)
  // or the raw `detail` object. For object payloads, pull `.message`
  // when it's a string; otherwise show the fallback title so users
  // never see "[object Object]" in the toast description.
  let description = fallbackTitle;
  if (typeof error === "string" && error) {
    description = error;
  } else if (
    errorObj &&
    typeof errorObj === "object" &&
    typeof errorObj.message === "string" &&
    errorObj.message
  ) {
    description = errorObj.message;
  }
  onTracked?.(description);
  toast({ title: fallbackTitle, description, variant: "destructive" });
};
